import { AppConfigService, DictionaryService } from '@cudirect/shared-ui-library';
import { LenderService } from '@shared/service/lender/lender.service';
import { AlertInfo, OIDLoginWidgetExportData, LoginWidgetContextData } from '@cudirect/ea-login';
import { Injectable } from '@angular/core';

import { LenderConfiguration } from '@shared/model/lender-config.model';

export type ActualTypes = 'string' | 'object' | 'boolean' | 'null' | 'array' | 'number' | 'bigint';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  // The stored login widget export data.
  private loginData: OIDLoginWidgetExportData = null;

  // A login widget alert can be stored here and retrieved by another instance.
  private loginAlert: AlertInfo;

  // Lender config is needed here to populate portions of the widget context.
  public lenderConfig: LenderConfiguration;

  public eConsentContent = null;

  // The base context
  private loginWidgetContextBase: LoginWidgetContextData = {
    isAgnosticMode: false,
    config: {
      dictionaryPath: null,
      preLoadTenantConfig: true,
      optionalOidcRedirectId: '',
      emailMaxLength: '50'
    },
    tokenReplacements: {
      'econsent-url': '/legal/econsent-only',
      'econsent-terms-url': '/terms-and-policy',
      'econsent-privacy-url': '/terms-and-policy'
    },
    loginFlow: {
      customLink: '/user/login'
    },
    oid: {
      oidConfigName: '',
      apiBaseURL: this.configService?.settings?.oid.apiBaseURL
    }
  };

  // The login context
  private readonly loginWidgetContextUserLogin: Partial<LoginWidgetContextData> = {
    createNewFlow: {
      enabled: false
    }
  };

  // The heavy reg context
  private readonly loginWidgetContextRegistration: Partial<LoginWidgetContextData> = {
    deepLink: 'create',
    createNewFlow: {
      enabled: true,
      customLink: null,
      deferredCreationState: 'defer',
      hideAuthenicationFlow: 'SSO',
      optionalFields: {
        enableSSN: true,
        enableDOB: true
      }
    },
    eConsentFlow: {
      enabled: true,
      viewRequired: false
    }
  };

  // The light reg context
  private readonly loginWidgetContextSSOLight: Partial<LoginWidgetContextData> = {
    deepLink: 'login',
    createNewFlow: {
      enabled: false
    }
  };

  // The verify context
  private readonly loginWidgetVerifyMFA: Partial<LoginWidgetContextData> = {
    deepLink: 'oid-verify'
  };

  constructor(
    private readonly lenderService: LenderService,
    private readonly configService: AppConfigService,
    private readonly dictionary: DictionaryService
  ) {
    this.lenderConfig = lenderService.lenderDataInfo;

    this.loginWidgetContextBase.oid.oidConfigName = this.lenderConfig?.oidConfigName
      ? this.lenderConfig?.oidConfigName
      : this.configService?.settings?.oid.oidConfigName;

    const lenderPhone = this.getLenderPhone();

    // Add in the lenderConfig specific data to the token replacements.
    this.loginWidgetContextBase.tokenReplacements = {
      ...this.loginWidgetContextBase.tokenReplacements,
      'admin-name': `<strong>${this.lenderConfig?.lenderName}</strong>`,
      'admin-phone': `<a href="tel:${lenderPhone}" id="admin-phone">${lenderPhone}</a>`,
      'admin-email': `<a href="mailto:${this.lenderConfig?.lenderEmail}" id="admin-email">${this.lenderConfig?.lenderEmail}</a>`
    };

    // Pulls from a config.json, but replacements convert booleans to strings, this protects against that.
    this.loginWidgetContextBase.oid.dxMode = String(this.configService.settings?.oid?.dxMode) === 'true';
  }

  // Setter, getter, and clear for Export Data. Probably not needed when refactor is done.
  public setLoginData(data: OIDLoginWidgetExportData): void {
    this.loginData = data;
  }

  // Retrieves stored login export data. Used for switching between instances of the widget.
  public getLoginData(): OIDLoginWidgetExportData {
    return this.loginData;
  }

  // Clears the stored login data.
  public clearLoginData(): void {
    this.loginData = null;
  }

  // Setter, getter, and clear for login alerts.
  public setLoginAlert(data: AlertInfo): void {
    this.loginAlert = data;
  }

  public getLoginAlert(): AlertInfo {
    return this.loginAlert;
  }

  public clearLoginAlert(): void {
    this.loginAlert = null;
  }

  /**
   * Helper method to construct the lender phone number. Used in the widget context.
   * @returns The phone number
   */
  public getLenderPhone(): string {
    if (this.lenderConfig && this.lenderConfig.contacts) {
      // Get first contact with type phone
      const firstPhone = this.lenderConfig.contacts.filter((contact) => contact.type.toLowerCase() === 'phone')[0];
      if (!firstPhone) {
        // TODO: In this case, do we want some fallback phone number?
        return;
      }
      const phoneExt = firstPhone.additionalValue ? ', Ext. ' + firstPhone.additionalValue : '';
      const phoneDesc = firstPhone.description ? ' (' + firstPhone.description + ')' : '';
      const phoneContact = firstPhone.value + phoneExt + phoneDesc;
      return phoneContact;
    }
  }

  /**
   * Returns the base widget context plus any additional params based on the mode.
   * Also makes any dictionary adjustments neccessary
   * @param mode The login mode (Determined by the url)
   * @param extraParams Any extraparams to be spread in. These can be partial objects
   * and will not overwrite the other properties in that object.
   * @returns the complete login widget context in a promise.
   */
  public async getWidgetContext(mode: 'heavyReg' | 'lightReg' | 'login' | 'verifyMFA', extraParams = {}): Promise<LoginWidgetContextData> {
    let modeParams;
    switch (mode) {
      case 'login':
        modeParams = this.loginWidgetContextUserLogin;
        this.dictionary.updateJSON({
          'ea-authenticate-oid-login': {
            title: 'Welcome back',
            userId: {
              label: 'Email address or phone number'
            },
            errors: {
              idInvalid: 'Please enter a valid email address or phone number.'
            }
          }
        });
        break;
      case 'lightReg':
        this.dictionary.updateJSON({
          'ea-authenticate-oid-login': {
            title: "For security purposes, let's verify your identity",
            userId: {
              label: 'Email address'
            },
            errors: {
              idInvalid: 'Please enter a valid email address.'
            }
          }
        });
        modeParams = this.loginWidgetContextSSOLight;
        break;
      case 'heavyReg':
        this.dictionary.updateJSON({
          'ea-authenticate-oid-login': {
            title: 'Welcome back',
            userId: {
              label: 'Email address or phone number'
            },
            errors: {
              idInvalid: 'Please enter a valid email address or phone number.'
            }
          }
        });
        this.loginWidgetContextRegistration.createNewFlow.displayOptionalSSNMessage = this.lenderConfig?.enableRegistrationOptionalSSN;
        modeParams = this.loginWidgetContextRegistration;
        this.loginWidgetContextRegistration.eConsentFlow = {
          enabled: true
        };
        break;
      case 'verifyMFA':
        modeParams = this.loginWidgetVerifyMFA;
        break;
      default:
        this.dictionary.updateJSON({
          'ea-authenticate-oid-login': {
            title: 'Welcome back',
            userId: {
              label: 'Email address or phone number'
            },
            errors: {
              idInvalid: 'Please enter a valid email address or phone number.'
            }
          }
        });
        modeParams = this.loginWidgetContextUserLogin;
    }
    let returnedContext = { ...this.loginWidgetContextBase, ...modeParams };
    if (extraParams) {
      // Iterate thru extra params. If value is an object, spread it in to the existing obj. Otherwise, spread as normal.
      // If login widget context ever uses an array, that will have to be accounted for here.
      for (const [keyName, param] of Object.entries(extraParams)) {
        const paramType = this.actualTypeOf(param);

        if (paramType === 'object') {
          returnedContext[keyName] = { ...returnedContext[keyName], ...(param as object) };
        } else {
          const reconstructed = { [keyName]: param };
          returnedContext = { ...returnedContext, ...reconstructed };
        }
      }
    }

    return Promise.resolve(returnedContext);
  }

  // A helper method to distiguish between object types. TODO: This is now in a
  // couple projects. Possible candidate for shared-ui?
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public actualTypeOf(data: any): ActualTypes {
    let dataType;
    const currentType = typeof data;
    switch (currentType) {
      case 'object':
        if (Array.isArray(data)) {
          dataType = 'array';
        } else if (data === null) {
          dataType = 'null';
        } else {
          dataType = 'object';
        }
        break;
      case 'number':
      case 'boolean':
      case 'string':
      default:
        dataType = currentType;
    }
    return dataType;
  }
}
